import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articleContents } from '@/mocks/articleContent';
import { mainSiteUrl } from '@/mocks/categories';
import { articlePreviews, articleImages } from '@/mocks/articles';
import NotFound from '@/pages/NotFound';
import { SITE_URL } from '@/constants';

const siteUrl = SITE_URL;

function BreadcrumbStructuredData({ title, slug }: { title: string; slug: string }) {
  const data = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${siteUrl}/` },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Guides',
        item: `${siteUrl}/#guides`,
      },
      { '@type': 'ListItem', position: 3, name: title, item: `${siteUrl}/guide/${slug}` },
    ],
  };
  return (
    <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }} />
  );
}

function ArticleStructuredData({ article, imageUrl }: { article: typeof articleContents[string]; imageUrl: string }) {
  const data = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    datePublished: '2026-06-15',
    dateModified: '2026-06-20',
    author: { '@type': 'Person', name: article.author },
    publisher: {
      '@type': 'Organization',
      name: 'SugargooSpreadsheet.link',
      url: siteUrl,
    },
    description: article.excerpt,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${siteUrl}/guide/${article.slug}`,
    },
    image: {
      '@type': 'ImageObject',
      url: imageUrl,
      width: '660',
      height: '440',
    },
  };
  return (
    <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }} />
  );
}

function FaqStructuredData({ sections }: { sections: typeof articleContents[string]['sections'] }) {
  const faqSection = sections.find((s) => s.type === 'faq');
  if (!faqSection || !('items' in faqSection)) return null;
  const data = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: (faqSection.items as { question: string; answer: string }[]).map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: { '@type': 'Answer', text: item.answer },
    })),
  };
  return (
    <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }} />
  );
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const effectiveSlug = slug || 'what-is-sugargoo-spreadsheet';
  const article = articleContents[effectiveSlug];

  const preview = articlePreviews.find((p) => p.slug === effectiveSlug);
  const articleImageUrl = preview ? articleImages[preview.imageSeq] : '';

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [effectiveSlug]);

  if (!article) {
    return <NotFound />;
  }

  const relatedArticles = articlePreviews
    .filter((a) => a.slug !== effectiveSlug)
    .slice(0, 4);

  return (
    <>
      <FaqStructuredData sections={article.sections} />
      <ArticleStructuredData article={article} imageUrl={articleImageUrl} />
      <BreadcrumbStructuredData title={article.title} slug={effectiveSlug} />
      <Navbar />
      <main className="pt-20 md:pt-24">
        <article className="w-full bg-background-50">
          <div className="w-full px-4 md:px-8 py-10 md:py-14">
            <div className="max-w-7xl mx-auto">
              <div className="flex flex-col lg:flex-row gap-10 lg:gap-16">
                {/* Sidebar */}
                <aside className="lg:w-[30%] order-2 lg:order-1">
                  <div className="lg:sticky lg:top-24">
                    <a
                      href={mainSiteUrl}
                      target="_blank"
                      rel="noopener nofollow"
                      className="inline-flex items-center gap-2 w-full justify-center px-6 py-3.5 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer mb-6"
                    >
                      Go to Main Site
                      <i className="ri-arrow-right-up-line"></i>
                    </a>

                    <div className="bg-background-100 rounded-2xl p-5">
                      <h4 className="text-sm font-semibold text-foreground-900 mb-4">
                        Related Articles
                      </h4>
                      <div className="flex flex-col gap-3">
                        {relatedArticles.map((ra) => (
                          <Link
                            key={ra.slug}
                            to={`/guide/${ra.slug}`}
                            className="text-sm text-foreground-600 hover:text-primary-500 transition-colors leading-snug cursor-pointer"
                          >
                            {ra.title}
                          </Link>
                        ))}
                        <Link
                          to="/"
                          className="text-sm text-accent-600 hover:text-accent-700 transition-colors font-medium mt-1 cursor-pointer"
                        >
                          &larr; Back to Homepage
                        </Link>
                      </div>
                    </div>
                  </div>
                </aside>

                {/* Main content */}
                <div className="lg:w-[70%] order-1 lg:order-2">
                  <nav className="text-xs text-foreground-400 mb-6 flex items-center gap-2 flex-wrap">
                    <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">
                      Home
                    </Link>
                    <span>/</span>
                    <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">
                      Guides
                    </Link>
                    <span>/</span>
                    <span className="text-foreground-600">{article.title}</span>
                  </nav>

                  <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 leading-tight mb-6">
                    {article.title}
                  </h1>

                  <div className="flex items-center gap-3 mb-8 pb-6 border-b border-background-200/70">
                    <div className="w-9 h-9 rounded-full bg-secondary-200 flex items-center justify-center">
                      <i className="ri-user-line text-foreground-500 text-sm"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-800">
                        {article.author}
                      </p>
                      <p className="text-xs text-foreground-400">
                        Updated {article.date} &middot; {article.readTime}
                      </p>
                    </div>
                  </div>

                  <div className="prose-custom text-foreground-700 leading-relaxed">
                    {article.sections.map((section, idx) => {
                      if (section.type === 'h2') {
                        return (
                          <h2
                            key={idx}
                            className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-10 mb-4"
                          >
                            {section.content}
                          </h2>
                        );
                      }
                      if (section.type === 'h3') {
                        return (
                          <h3
                            key={idx}
                            className="font-heading text-lg md:text-xl font-semibold text-foreground-800 mt-8 mb-3"
                          >
                            {section.content}
                          </h3>
                        );
                      }
                      if (section.type === 'p') {
                        return (
                          <p
                            key={idx}
                            className="text-sm md:text-base text-foreground-700 mb-4 leading-relaxed"
                          >
                            {section.content}
                          </p>
                        );
                      }
                      if (section.type === 'cta') {
                        return (
                          <div
                            key={idx}
                            className="my-8 p-5 bg-primary-50 border border-primary-200/50 rounded-2xl"
                          >
                            <p className="text-sm text-primary-700 mb-3 font-medium">
                              {section.content}
                            </p>
                            <a
                              href={mainSiteUrl}
                              target="_blank"
                              rel="noopener nofollow"
                              className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                            >
                              Browse Spreadsheet
                              <i className="ri-arrow-right-up-line"></i>
                            </a>
                          </div>
                        );
                      }
                      if (section.type === 'table') {
                        return (
                          <div key={idx} className="my-8 overflow-x-auto">
                            <table className="w-full border-collapse text-sm">
                              <thead>
                                <tr className="border-b border-foreground-200">
                                  {(section.headers as string[]).map((h, hi) => (
                                    <th
                                      key={hi}
                                      className="text-left py-3 px-4 font-semibold text-foreground-900 bg-background-100 first:rounded-tl-lg last:rounded-tr-lg whitespace-nowrap"
                                    >
                                      {h}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {(section.rows as string[][]).map((row, ri) => (
                                  <tr
                                    key={ri}
                                    className="border-b border-background-200/70 hover:bg-background-50 transition-colors"
                                  >
                                    {row.map((cell, ci) => (
                                      <td key={ci} className="py-3 px-4 text-foreground-700">
                                        {cell}
                                      </td>
                                    ))}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        );
                      }
                      if (section.type === 'faq') {
                        return (
                          <div key={idx} className="mt-10 space-y-3">
                            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6">
                              Frequently Asked Questions
                            </h2>
                            {'items' in section &&
                              section.items.map(
                                (faq: { question: string; answer: string }, fi: number) => (
                                  <details
                                    key={fi}
                                    className="bg-background-100 rounded-xl overflow-hidden group"
                                  >
                                    <summary className="px-5 py-4 text-sm font-medium text-foreground-800 cursor-pointer hover:text-primary-500 transition-colors flex items-center justify-between">
                                      {faq.question}
                                      <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform flex-shrink-0 ml-3"></i>
                                    </summary>
                                    <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                                      {faq.answer}
                                    </div>
                                  </details>
                                )
                              )}
                          </div>
                        );
                      }
                      return null;
                    })}
                  </div>

                  <div className="mt-12 pt-8 border-t border-background-200/70">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                      <div>
                        <p className="text-sm text-foreground-500 mb-1">
                          Explore more Sugargoo Spreadsheet resources:
                        </p>
                        <Link
                          to="/"
                          className="text-sm text-primary-500 hover:text-primary-600 font-medium cursor-pointer"
                        >
                          &larr; Return to Sugargoo Spreadsheet Homepage
                        </Link>
                      </div>
                      <a
                        href={mainSiteUrl}
                        target="_blank"
                        rel="noopener nofollow"
                        className="inline-flex items-center gap-2 px-6 py-3 bg-foreground-900 text-background-50 rounded-full text-sm font-semibold hover:bg-foreground-800 transition-colors whitespace-nowrap cursor-pointer"
                      >
                        Sugargoo Spreadsheet
                        <i className="ri-arrow-right-up-line"></i>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}